import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Chart as ChartJS } from 'chart.js/auto';
import { Line } from 'react-chartjs-2';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom delivery truck icon - Blue delivery truck
const deliveryIcon = new L.DivIcon({
    html: '<div style="background-color: #007bff; color: white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-size: 20px; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">🚚</div>',
    className: 'custom-delivery-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
});

// Custom return truck icon - Orange return truck
const returnIcon = new L.DivIcon({
    html: '<div style="background-color: #ff6700; color: white; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-size: 20px; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">🔄</div>',
    className: 'custom-return-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
});

const API_URL = "https://sunrise-backend.sunrisemeditechsystems.com/api";

export default function Home() {
    const [userData, setUserData] = useState(null);
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [statistics, setStatistics] = useState({
        totalOrders: 0,
        totalDeliveries: 0,
        totalProducts: 0,
        pendingDeliveries: 0,
        returnRequests: 0,
        totalRevenue: 0,
        activeDeliveries: []
    });
    const [salesData, setSalesData] = useState({
        labels: [],
        datasets: []
    });
    const [activeDeliveries, setActiveDeliveries] = useState([]);
    const navigate = useNavigate();

    // First, update the useEffect where userData is set
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userDataStr = localStorage.getItem('userData');
        if (!token) {
            navigate('/login');
            return;
        }
        if (userDataStr) {
            const user = JSON.parse(userDataStr);
            setUserData(user);
            // Set welcome message based on role
            let message = '';
            switch (user.role) {
                case 'admin':
                    message = 'You have full access to manage products, orders, and staff.';
                    break;
                case 'receptionist':
                    message = 'You can manage customer orders and view products.';
                    break;
                case 'deliveryman':
                    message = 'You can view and manage your delivery assignments.';
                    break;
                default:
                    message = 'Welcome to Sunrise Medicare Platform.';
            }
            setWelcomeMessage(message);
            fetchDashboardData(user.role);
        }
    }, [navigate]);
    
    // Add real-time updates for active deliveries
    useEffect(() => {
        if (!userData) return;
        
        // Refresh active deliveries every 30 seconds
        const interval = setInterval(() => {
            if (userData.role === 'admin' || userData.role === 'receptionist') {
                const token = localStorage.getItem('token');
                if (token) {
                    axios.get(`${API_URL}/orders/active-deliveries`, { 
                        headers: { Authorization: `Bearer ${token}` } 
                    })
                    .then(response => {
                        setActiveDeliveries(response.data);
                    })
                    .catch(error => {
                        console.error('Error refreshing active deliveries:', error);
                    });
                }
            }
        }, 30000); // 30 seconds
        
        return () => clearInterval(interval);
    }, [userData]);
    
    // Update the welcome section JSX
    <div className="row mb-4">
        <div className="col-12">
            <div className="card bg-primary text-white">
                <div className="card-body">
                    <h4 className="mt-0 mb-3">
                        Welcome, {userData?.username || userData?.name} ({userData?.role})!
                    </h4>
                    <p className="mb-0">{welcomeMessage}</p>
                </div>
            </div>
        </div>
    </div>

    const fetchDashboardData = async (role) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            console.log('Fetching dashboard data for role:', role);
            console.log('Token exists:', !!token);

            if (role === 'admin' || role === 'receptionist') {
                console.log('Fetching admin/receptionist data...');
                
                // Fetch data individually to handle failures better
                try {
                    const ordersRes = await axios.get(`${API_URL}/order/statistics`, { headers });
                    console.log('Orders response:', ordersRes.data);
                    
                    const productsRes = await axios.get(`${API_URL}/product/statistics`, { headers });
                    console.log('Products response:', productsRes.data);
                    
                    const revenueRes = await axios.get(`${API_URL}/order/revenue`, { headers });
                    console.log('Revenue response:', revenueRes.data);
                    
                    const salesRes = await axios.get(`${API_URL}/order/sales-chart`, { headers });
                    console.log('Sales response:', salesRes.data);
                    
                    const deliveriesRes = await axios.get(`${API_URL}/order/active-deliveries`, { headers });
                    console.log('Deliveries response:', deliveriesRes.data);

                    setStatistics(prev => ({
                        ...prev,
                        totalOrders: ordersRes.data.total || 0,
                        totalProducts: productsRes.data.total || 0,
                        totalRevenue: revenueRes.data.total || 0,
                        pendingDeliveries: ordersRes.data.pending || 0,
                        returnRequests: ordersRes.data.returns || 0
                    }));

                    setSalesData(salesRes.data);
                    setActiveDeliveries(deliveriesRes.data);
                } catch (apiError) {
                    console.error('API call failed:', apiError);
                    console.error('Error response:', apiError.response?.data);
                }
            }

            if (role === 'deliveryman') {
                console.log('Fetching deliveryman data...');
                const deliveriesRes = await axios.get(`${API_URL}/order/assigned/statistics`, { headers });
                console.log('Deliveryman API Response:', deliveriesRes.data);
                
                setStatistics(prev => ({
                    ...prev,
                    totalDeliveries: deliveriesRes.data.total || 0,
                    pendingDeliveries: deliveriesRes.data.pending || 0,
                    activeDeliveries: deliveriesRes.data.active || []
                }));
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            console.error('Error details:', error.response?.data);
            // Set default values if API fails
            setStatistics(prev => ({
                ...prev,
                totalOrders: 0,
                totalDeliveries: 0,
                totalProducts: 0,
                pendingDeliveries: 0,
                returnRequests: 0,
                totalRevenue: 0
            }));
        }
    };

    return (
        <div className="content-page">
            <div className="content">
                <div className="container-fluid">
                    {/* Welcome Section */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card bg-primary text-white">
                                <div className="card-body">
                                    <h4 className="mt-0 mb-3">
                                        Welcome, {userData?.username || userData?.name} ({userData?.role})!
                                    </h4>
                                    <p className="mb-0">{welcomeMessage}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Statistics Cards */}
                    <div className="row">
                        {(userData?.role === 'admin' || userData?.role === 'receptionist') && (
                            <>
                                <div className="col-md-2">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1">{statistics.totalOrders}</h4>
                                                    <p className="text-muted mb-0">Total Orders</p>
                                                </div>
                                                <div className="avatar bg-primary">
                                                    <i className="mdi mdi-package text-white font-size-20"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-2">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1">₹{statistics.totalRevenue.toLocaleString()}</h4>
                                                    <p className="text-muted mb-0">Total Revenue</p>
                                                </div>
                                                <div className="avatar bg-success">
                                                    <i className="mdi mdi-currency-inr text-white font-size-20"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-2">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1">{statistics.totalProducts}</h4>
                                                    <p className="text-muted mb-0">Total Products</p>
                                                </div>
                                                <div className="avatar bg-info">
                                                    <i className="mdi mdi-medical-bag text-white font-size-20"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-2">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1">{statistics.returnRequests}</h4>
                                                    <p className="text-muted mb-0">Return Requests</p>
                                                </div>
                                                <div className="avatar bg-warning">
                                                    <i className="mdi mdi-undo text-white font-size-20"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-2">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1">{activeDeliveries.length}</h4>
                                                    <p className="text-muted mb-0">Active Deliveries</p>
                                                </div>
                                                <div className="avatar bg-info">
                                                    <i className="mdi mdi-truck-delivery text-white font-size-20"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {userData?.role === 'deliveryman' && (
                            <>
                                <div className="col-md-4">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1">{statistics.totalDeliveries}</h4>
                                                    <p className="text-muted mb-0">Total Deliveries</p>
                                                </div>
                                                <div className="avatar bg-primary">
                                                    <i className="mdi mdi-truck-delivery text-white font-size-20"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1">{statistics.pendingDeliveries}</h4>
                                                    <p className="text-muted mb-0">Pending Deliveries</p>
                                                </div>
                                                <div className="avatar bg-warning">
                                                    <i className="mdi mdi-clock-outline text-white font-size-20"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Charts and Maps Section */}
                    <div className="row">
                        {/* Sales Chart for Admin/Receptionist */}
                        {(userData?.role === 'admin' || userData?.role === 'receptionist') && (
                            <div className="col-xl-8">
                                <div className="card">
                                    <div className="card-body">
                                        <h4 className="card-title mb-4">Sales Overview</h4>
                                        <div style={{ height: '300px', position: 'relative' }}>
                                            <Line 
                                                data={salesData} 
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            ticks: {
                                                                font: {
                                                                    size: 12
                                                                }
                                                            }
                                                        },
                                                        x: {
                                                            ticks: {
                                                                font: {
                                                                    size: 12
                                                                }
                                                            }
                                                        }
                                                    },
                                                    plugins: {
                                                        legend: {
                                                            position: 'top',
                                                            labels: {
                                                                boxWidth: 20,
                                                                font: {
                                                                    size: 12
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Live Delivery Map */}
                        {activeDeliveries.length > 0 && (
                            <div className={userData?.role === 'deliveryman' ? "col-12" : "col-xl-4"}>
                                <div className="card">
                                    <div className="card-body">
                                        <h4 className="card-title mb-4">Live Delivery Map</h4>
                                        <div style={{ height: "400px", width: "100%" }}>
                                            <MapContainer
                                                center={[23.0225, 72.5714]}
                                                zoom={12}
                                                style={{ height: "100%", width: "100%" }}
                                            >
                                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                {activeDeliveries.map((delivery) => (
                                                    <Marker
                                                        key={delivery.id}
                                                        position={[
                                                            delivery.delivery_location.latitude, 
                                                            delivery.delivery_location.longitude
                                                        ]}
                                                        icon={delivery.status === 'out_for_delivery' ? deliveryIcon : returnIcon}
                                                    >
                                                        <Popup>
                                                            <div className="p-2">
                                                                <div className="d-flex align-items-center mb-2">
                                                                    <i className={`mdi ${delivery.status === 'out_for_delivery' ? 'mdi-truck-delivery text-primary' : 'mdi-truck-delivery text-warning'} me-2`}></i>
                                                                    <strong>Order #{delivery.id}</strong>
                                                                </div>
                                                                <div className="mb-1">
                                                                    <span className={`badge ${delivery.status === 'out_for_delivery' ? 'bg-primary' : 'bg-warning'} text-white`}>
                                                                        {delivery.status === 'out_for_delivery' ? '🚚 Out for Delivery' : '🔄 Out for Return'}
                                                                    </span>
                                                                </div>
                                                                <div className="mb-1">
                                                                    <strong>Deliveryman:</strong> {delivery.assigned_deliveryman?.name || delivery.assigned_deliveryman?.username || 'Unknown'}
                                                                </div>
                                                                <div className="mb-1">
                                                                    <strong>Customer:</strong> {
                                                                        delivery.source === 'sunrise-website' 
                                                                            ? `${delivery.shipping_address?.firstName || ''} ${delivery.shipping_address?.lastName || ''}`
                                                                            : `${delivery.shipping_address?.doctorName || 'Doctor'} - ${delivery.shipping_address?.hospitalName || 'Hospital'}`
                                                                    }
                                                                </div>
                                                                <div className="mb-1">
                                                                    <strong>Product:</strong> {delivery.items[0]?.product || 'N/A'}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    <i className="mdi mdi-clock-outline me-1"></i>
                                                                    Last Updated: {new Date(delivery.delivery_location.updated_at).toLocaleTimeString()}
                                                                </div>
                                                            </div>
                                                        </Popup>
                                                    </Marker>
                                                ))}
                                            </MapContainer>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}